import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import { ContactHelper } from 'src/app/helpers/contact-helper';
import ProjectHelper from 'src/app/helpers/project-helper';
import Company from 'src/app/model/company/company';
import Contact from 'src/app/model/Contact/contact';
import { Project } from 'src/app/model/project/project';
import {
  SubProject,
  SUB_PROJECT_TYPES
} from 'src/app/model/project/sub-project';
import { FormatPhoneNumber } from 'src/app/modules/app-pipes/format-phonenumber';
import {
  GroupProjectContactPipe,
  ProjectContactGroup
} from 'src/app/pipes/group-project-contact';
import { ProjectDisplayObservableService } from 'src/app/services/project-display-observable.service';
import { SubscriptionBasedComponent } from 'src/app/subscription-helpers/subscription-based.component';
import * as moment from 'moment-timezone';

@Component({
  selector: 'client-project-form-summary-banner',
  templateUrl: './client-project-form-summary-banner.component.html',
  styleUrls: ['./client-project-form-summary-banner.component.scss'],
  providers: [GroupProjectContactPipe, FormatPhoneNumber]
})
export class ClientProjectFormSummaryBanner extends SubscriptionBasedComponent
  implements OnInit, OnDestroy {
  @Input()
  project: Project;

  @Input()
  subProject: SubProject;

  createdMoment: moment.Moment;
  momentFormat: string;

  projectContactGroups: ProjectContactGroup[];
  selectedOtherContacts: Contact[];
  projectContactToCompany = new Map<number, Company>();

  showExtraCompanyContacts = false;

  backgroundClass: string;

  projectContactToCompanySubject = new BehaviorSubject<Map<number, Company>>(
    this.projectContactToCompany
  );

  constructor(
    private projectDisplayObservableService: ProjectDisplayObservableService,
    private groupProjectContactPipe: GroupProjectContactPipe,
    private formatPhoneNumber: FormatPhoneNumber
  ) {
    super();
  }

  ngOnInit(): void {
    this.backgroundClass =
      this.subProject.type === SUB_PROJECT_TYPES.BASE_PROJECT
        ? 'plaatsing'
        : ProjectHelper.getTypeStatusString(this.subProject);
    this.initializeDisplayDate();
    this.calculateProjectContactGroups();
    this.calculateShowExtraCompanyContacts();

    this.addSubscription(
      this.projectDisplayObservableService.getProjectContactsChangedSubscribable(),
      () => {
        this.calculateProjectContactGroups();
        this.calculateShowExtraCompanyContacts();
      }
    );
    this.addSubscription(
      this.projectDisplayObservableService.getSelectedOtherContactsChangedSubscribable(),
      (contacts: Contact[]) => {
        this.selectedOtherContacts = contacts;
        this.calculateProjectContactGroups();
      }
    );
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.projectContactToCompanySubject != null) {
      this.projectContactToCompanySubject.complete();
    }
  }

  private initializeDisplayDate(): void {
    this.createdMoment =
      this.subProject.id != null
        ? moment(this.subProject.currentCreatedAt)
            .tz('Europe/Brussels')
            .locale('nl-BE')
        : moment()
            .tz('Europe/Brussels')
            .locale('nl-BE');
    this.momentFormat =
      this.subProject.id != null ? 'DD/MM/YYYY - HH:mmu' : 'DD/MM/YYYY';
  }

  private calculateProjectContactGroups(): void {
    const projectContactGroups = this.groupProjectContactPipe.transform(
      this.project.projectContacts
    );
    if (
      CollectionHelper.isPopulated(this.selectedOtherContacts) &&
      CollectionHelper.isPopulated(projectContactGroups)
    ) {
      const firstGroup = projectContactGroups[0];
      if (firstGroup.contacts == null) {
        firstGroup.contacts = [];
      }
      firstGroup.contacts.push(...this.selectedOtherContacts);
    }
    if (CollectionHelper.isPopulated(projectContactGroups)) {
      projectContactGroups.forEach(pcg => {
        if (CollectionHelper.isPopulated(pcg.contacts)) {
          pcg.contacts.forEach(c =>
            ContactHelper.setFormDisplays(c, this.formatPhoneNumber)
          );
        }
      });
    }
    this.projectContactGroups = projectContactGroups;
    this.calculateProjectContactToCompany();
  }

  private calculateProjectContactToCompany(): void {
    this.projectContactToCompany.clear();
    if (CollectionHelper.isPopulated(this.projectContactGroups)) {
      this.projectContactGroups.forEach((pcg: ProjectContactGroup) => {
        if (CollectionHelper.isPopulated(pcg.contacts)) {
          pcg.contacts.forEach((c: Contact) => {
            this.projectContactToCompany.set(c.id, pcg.company);
          });
        }
      });
    }
    this.projectContactToCompanySubject.next(this.projectContactToCompany);
  }

  private calculateShowExtraCompanyContacts(): void {
    this.showExtraCompanyContacts =
      this.project.collaborative &&
      CollectionHelper.isPopulated(this.project.projectExtraCompanyContacts) &&
      !ProjectHelper.multipleInvolvedCompanies(this.project);
  }
}
